import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Spinner } from 'flowbite-react';
import { HiCheckCircle, HiExclamation, HiXCircle } from 'react-icons/hi';
import {
  organizationActivityApi,
  type ActivityEventKind,
  type OrganizationActivityDetail,
  type PaginatedOrganizationActivityEvents,
} from '../../api/activityLog';
import { getUserErrorMessage } from '../../api';
import { ActivityDetailModal } from '../activity/ActivityDetailModal';
import {
  ACTOR_KIND_CLASSES,
  ROLE_LABELS,
  STATUS_META,
  WINDOW_OPTIONS,
  actorKindLabel,
  calendarLabel,
  formatDateTime,
  formatRelative,
  organizationStaffLabel,
  statusLegend,
} from '../activity/activityDisplay';
import type { LEAResponse } from '../../types/api';

interface ActivityTabProps {
  lea: LEAResponse;
}

const EVENT_FILTERS: Array<{ kind: ActivityEventKind; label: string }> = [
  { kind: 'all', label: 'All' },
  { kind: 'edits', label: 'Edits' },
  { kind: 'signIns', label: 'Sign-ins' },
];

const PAGE_SIZE = 25;

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
    <h4 className="mb-2 text-sm font-semibold text-gray-500 dark:text-gray-400">{title}</h4>
    <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">{children}</div>
  </div>
);

const Big: React.FC<{ value: number | string; label: string }> = ({ value, label }) => (
  <p>
    <span className="text-2xl font-semibold text-gray-900 dark:text-white">{value}</span>{' '}
    <span className="text-gray-500 dark:text-gray-400">{label}</span>
  </p>
);

const Check: React.FC<{ ok: boolean; children: React.ReactNode }> = ({ ok, children }) => (
  <p className="flex items-center gap-1.5">
    {ok
      ? <HiCheckCircle className="h-4 w-4 flex-shrink-0 text-green-600 dark:text-green-400" />
      : <HiXCircle className="h-4 w-4 flex-shrink-0 text-red-500 dark:text-red-400" />}
    {children}
  </p>
);

/**
 * Super-admin view of what's happened to an organization's directory data,
 * and how its own people are engaging. See docs/features/ORGANIZATION_ACTIVITY.md.
 */
export const ActivityTab: React.FC<ActivityTabProps> = ({ lea }) => {
  const orgId = lea.educationOrganizationId;
  const [days, setDays] = useState(90);
  const [summary, setSummary] = useState<OrganizationActivityDetail | null>(null);
  const [events, setEvents] = useState<PaginatedOrganizationActivityEvents | null>(null);
  const [kind, setKind] = useState<ActivityEventKind>('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);

  const staffLabel = organizationStaffLabel(summary?.leaType ?? lea.leaType);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    organizationActivityApi.getSummary(orgId, days)
      .then(result => { if (!cancelled) { setSummary(result); setError(null); } })
      .catch(err => { if (!cancelled) setError(getUserErrorMessage(err, 'Failed to load activity')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, days]);

  const loadEvents = useCallback(async (nextPage: number) => {
    setEventsLoading(true);
    try {
      setEvents(await organizationActivityApi.listEvents(orgId, { days, kind, page: nextPage, pageSize: PAGE_SIZE }));
      setPage(nextPage);
    } catch (err) {
      setError(getUserErrorMessage(err, 'Failed to load activity'));
    } finally {
      setEventsLoading(false);
    }
  }, [orgId, days, kind]);

  useEffect(() => {
    loadEvents(1);
  }, [loadEvents]);

  if (loading && !summary) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const status = summary ? STATUS_META[summary.status] : null;

  return (
    <div className="space-y-6">
      {error && (
        <Alert color="failure" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Activity</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Edits to {lea.nameOfInstitution} and its schools, and sign-ins by its people. Visible to super admins only.
          </p>
        </div>
        <select
          aria-label="Time window"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        >
          {WINDOW_OPTIONS.map(option => (
            <option key={option.days} value={option.days}>{option.label}</option>
          ))}
        </select>
      </div>

      {summary && status && (
        <>
          {/* Status banner */}
          <div className="flex flex-wrap items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
            <span
              className={`rounded-full px-3 py-1 text-sm font-semibold ${status.className}`}
              title={statusLegend(summary.thresholds).map(([s, text]) => `${STATUS_META[s].label}: ${text}`).join('\n')}
            >
              {status.label}
            </span>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              <p>
                {summary.lastOrganizationEditAt
                  ? <>Last edit by {staffLabel.toLowerCase()}: <strong>{formatRelative(summary.lastOrganizationEditAt)}</strong>{summary.lastOrganizationEditBy && <>, by {summary.lastOrganizationEditBy}</>}.</>
                  : <>{staffLabel} haven't edited this organization's information yet.</>}
              </p>
              {summary.lastEditAt && summary.lastEditAt !== summary.lastOrganizationEditAt && (
                <p className="text-gray-500 dark:text-gray-400">
                  Most recent edit by anyone: {formatRelative(summary.lastEditAt)}{summary.lastEditBy && <>, by {summary.lastEditBy}</>}.
                </p>
              )}
            </div>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Card title={`Edits (${WINDOW_OPTIONS.find(o => o.days === days)?.label.toLowerCase() ?? `${days} days`})`}>
              <Big value={summary.organizationEdits} label={`by ${staffLabel.toLowerCase()}`} />
              <p>{summary.aiuEdits} by AIU staff{summary.systemEdits ? `, ${summary.systemEdits} by the system` : ''}</p>
              <p className="text-gray-500 dark:text-gray-400">
                {summary.editors} {summary.editors === 1 ? 'person' : 'people'} from here edited
              </p>
            </Card>

            <Card title="Sign-ins">
              <Big value={`${summary.signedInAccounts} of ${summary.accountCount}`} label="accounts signed in" />
              {summary.neverSignedIn > 0 && (
                <p className="flex items-center gap-1 text-amber-700 dark:text-amber-400">
                  <HiExclamation className="h-4 w-4" />
                  {summary.neverSignedIn} {summary.neverSignedIn === 1 ? 'has' : 'have'} never signed in
                </p>
              )}
            </Card>

            <Card title="Contacts">
              <Big value={summary.contacts} label="in the directory" />
              <p>
                {summary.roleTagGaps > 0
                  ? <Link to={`/leas/${lea.leaId}?tab=staff`} className="text-blue-600 hover:underline dark:text-blue-400">{summary.roleTagGaps} without role tags</Link>
                  : 'All have role tags'}
              </p>
              <p>
                {summary.staleContacts} not updated in over {Math.round(summary.thresholds.staleContactDays / 30.44)} months
              </p>
            </Card>

            <Card title="Basics">
              <Check ok={summary.hasSuperintendent}>Superintendent listed</Check>
              <Check ok={summary.boardMembers > 0}>{summary.boardMembers} board member{summary.boardMembers === 1 ? '' : 's'}</Check>
              {summary.calendarStatus && (
                <Check ok={summary.calendarStatus === 'complete'}>Calendar: {calendarLabel(summary)}</Check>
              )}
            </Card>
          </div>

          {/* Accounts */}
          <div>
            <h4 className="mb-2 font-semibold text-gray-700 dark:text-gray-300">People with accounts here</h4>
            {summary.accounts.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Nobody here has an account yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                    <tr>
                      <th className="px-4 py-2">Name</th>
                      <th className="px-4 py-2">Access</th>
                      <th className="px-4 py-2">Last seen</th>
                      <th className="px-4 py-2 text-right">Sign-ins</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {summary.accounts.map(account => (
                      <tr key={account.userId} className="bg-white dark:bg-gray-900">
                        <td className="px-4 py-2">
                          <Link to={`/persons/${account.personId}`} className="text-blue-600 hover:underline dark:text-blue-400">
                            {account.name}
                          </Link>
                        </td>
                        <td className="px-4 py-2 text-gray-600 dark:text-gray-400">{ROLE_LABELS[account.role] ?? account.role}</td>
                        <td className="px-4 py-2 text-gray-600 dark:text-gray-400" title={formatDateTime(account.lastSeenAt)}>
                          {formatRelative(account.lastSeenAt, new Date(), 'Never signed in')}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-400">{account.signIns}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Timeline */}
      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h4 className="font-semibold text-gray-700 dark:text-gray-300">Timeline</h4>
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700" role="group" aria-label="Filter timeline">
            {EVENT_FILTERS.map(filter => (
              <button
                key={filter.kind}
                type="button"
                onClick={() => setKind(filter.kind)}
                aria-pressed={kind === filter.kind}
                className={`px-3 py-1.5 text-sm first:rounded-l-lg last:rounded-r-lg ${
                  kind === filter.kind
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {eventsLoading && !events ? (
          <div className="flex justify-center py-6"><Spinner /></div>
        ) : !events || events.data.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Nothing in this window.</p>
        ) : (
          <>
            <ul className={`divide-y divide-gray-100 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700 ${eventsLoading ? 'opacity-60' : ''}`}>
              {events.data.map(event => (
                <li key={event.activityLogId}>
                  <button
                    type="button"
                    onClick={() => setDetailId(event.activityLogId)}
                    className="flex w-full flex-wrap items-baseline gap-x-3 gap-y-1 bg-white px-4 py-2.5 text-left text-sm hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-800"
                  >
                    <span className="w-32 flex-shrink-0 text-xs text-gray-500 dark:text-gray-400" title={formatDateTime(event.createdAt)}>
                      {formatRelative(event.createdAt)}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${ACTOR_KIND_CLASSES[event.actorKind]}`}>
                      {actorKindLabel(event.actorKind, summary?.leaType ?? lea.leaType)}
                    </span>
                    <span className="min-w-0 flex-1 text-gray-800 dark:text-gray-200">{event.actionDescription}</span>
                    {event.organizationName && event.educationOrganizationId !== orgId && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">{event.organizationName}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
            {events.totalPages > 1 && (
              <div className="mt-3 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                <span>Page {events.page} of {events.totalPages} ({events.totalCount} entries)</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={page <= 1 || eventsLoading}
                    onClick={() => loadEvents(page - 1)}
                    className="rounded-lg border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-700"
                  >
                    Newer
                  </button>
                  <button
                    type="button"
                    disabled={page >= events.totalPages || eventsLoading}
                    onClick={() => loadEvents(page + 1)}
                    className="rounded-lg border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-700"
                  >
                    Older
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ActivityDetailModal activityLogId={detailId} show={detailId !== null} onClose={() => setDetailId(null)} />
    </div>
  );
};

export default ActivityTab;
